<script setup lang="ts">
import { computed, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import {
  useLoadOkaneKollegeIndexPageQuery,
  useOkaneKollegeIndexPageQuery,
} from './OkaneKollegeIndexPage.generated'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import type { BreadCrumbLink } from '@/types/app'
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import OkaneKollegeUserList from './OkaneKollegeUserList/OkaneKollegeUserList.vue'
import OkaneKollegeIndexPageFilterConditions from './OkaneKollegeIndexPageFilterConditions/OkaneKollegeIndexPageFilterConditions.vue'
import type { KollegeUsersFilterConditions } from '@/types/schema.generated'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useOkaneKollegeIndexPageFilterConditions } from './schema'

const route = useRoute('OkaneKollegeIndex')

useHead({
  title: 'オカネカレッジ',
})

const breadcrumbs = computed<ReadonlyArray<BreadCrumbLink>>(() => {
  return route.meta.breadcrumbs ?? []
})

const { filterConditions, submitSearch, resetSearch } =
  useOkaneKollegeIndexPageFilterConditions()

const kollegeUserFilterConditions = computed<KollegeUsersFilterConditions>(
  () => {
    const trimmedUserName = filterConditions.value.userName.trim()

    return {
      ...(trimmedUserName.length > 0 ? { userName: trimmedUserName } : {}),
      ...(filterConditions.value.userStatuses.length > 0
        ? { userStatus: filterConditions.value.userStatuses }
        : {}),
      ...(filterConditions.value.instructorIds.length > 0
        ? { instructor: filterConditions.value.instructorIds }
        : {}),
      antiSocialStatus: filterConditions.value.isAntiSocial,
    }
  }
)

// ここのクエリは条件指定時に必要な情報を持ってくるクエリ
// 講師の一覧など
const { result: prefetchedResult } = useOkaneKollegeIndexPageQuery()

const { result, error, loading, refetch } = useLoadOkaneKollegeIndexPageQuery({
  // あとでreadmore実装する
  first: 9999,
  conditions: kollegeUserFilterConditions.value,
})
watch([result, error], ([result, error]) => {
  if (error) {
    throw new Error(error.message)
  }
  if (result == null) {
    throw new Error('result is null')
  }
})
onBeforeRouteUpdate(() => {
  refetch({
    first: 9999,
    conditions: kollegeUserFilterConditions.value,
  })
})
const userEdges = computed(() => result.value?.kollegeUsers.edges ?? [])
</script>

<template>
  <CenteredPageContainer :class="$style.page">
    <BreadcrumbList :links="breadcrumbs" />
    <main :class="[$style.page__main, $style.main]">
      <SectionTitle>オカネカレッジ</SectionTitle>
      <section :class="[$style.main__content, $style.content]">
        <OkaneKollegeIndexPageFilterConditions
          v-if="prefetchedResult"
          :payload="prefetchedResult"
          v-model:filter-conditions="filterConditions"
          :loading="loading"
          @search="submitSearch"
          @reset="resetSearch"
        />
        <OkaneKollegeUserList
          v-if="!loading"
          :userEdges="userEdges"
          :class="$style.content__list"
        />
        <div v-else :class="$style.content__loading"><LoadingIndicator /></div>
      </section>
    </main>
  </CenteredPageContainer>
</template>

<style lang="scss" module>
.page {
  &__main {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.main {
  &__content {
    background-color: $color-surface-primary;
    border-radius: $border-radius-v2-rounder;
    box-shadow: $shadow-24;
    padding: 24px;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.content {
  &__list {
    margin-top: 24px;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 600px;
  }
}
</style>
